import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUploadCertificate } from '@/hooks/use-certificates';
import { useTerminalProfiles } from '@/hooks/use-terminals';
import { useT } from '@/i18n/use-t';

function extractError(err: unknown): string | null {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string; code?: string } | undefined;
    if (body?.message) return body.code ? `${body.code}: ${body.message}` : body.message;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return null;
}

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return globalThis.btoa(binary);
}

function isoOrNull(local: string): string | null {
  if (local === '') return null;
  // <input type="date"> gives YYYY-MM-DD; promote to midnight UTC for the API.
  const d = new Date(`${local}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function CertificateNewPage() {
  const t = useT('admin_ui');
  const navigate = useNavigate();
  const upload = useUploadCertificate();
  const { data: profiles } = useTerminalProfiles();

  const [label, setLabel] = useState('');
  const [kind, setKind] = useState('');
  const [terminalProfileId, setTerminalProfileId] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [notBefore, setNotBefore] = useState('');
  const [notAfter, setNotAfter] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBanner(null);
    if (file === null) {
      setBanner(t('certificates.upload.noFile', 'Pick a certificate file before uploading.'));
      return;
    }
    try {
      const payloadBase64 = await fileToBase64(file);
      const created = await upload.mutateAsync({
        label: label.trim(),
        kind: kind.trim(),
        terminalProfileId: terminalProfileId === '' ? null : terminalProfileId,
        serialNumber: serialNumber.trim() === '' ? null : serialNumber.trim(),
        notBefore: isoOrNull(notBefore),
        notAfter: isoOrNull(notAfter),
        payloadBase64,
      });
      navigate(`/certificates/${created.id}`);
    } catch (err) {
      setBanner(extractError(err) ?? t('certificates.upload.failed', 'Upload failed.'));
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <Link to="/certificates" className="text-xs text-muted-foreground hover:underline">
          {t('certificates.nav.back', '← Certificates')}
        </Link>
        <h1 className="text-2xl font-semibold">
          {t('certificates.upload.title', 'Upload certificate')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'certificates.upload.encryptionNote',
            'The file is base64-encoded client-side and encrypted at rest server-side (pgp_sym_encrypt against',
          )}{' '}
          <code>CERT_ENCRYPTION_KEY</code>).
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="grid grid-cols-2 gap-4 rounded-md border border-border bg-card p-4"
      >
        <div>
          <Label htmlFor="label">{t('certificates.field.label', 'Label')}</Label>
          <Input
            id="label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="LT-VMI signing 2026"
            required
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="kind">{t('certificates.field.kind', 'Kind')}</Label>
          <Input
            id="kind"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            placeholder="lt-vmi-signing"
            required
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="terminalProfileId">
            {t('certificates.upload.bindProfile', 'Bind to terminal profile (optional)')}
          </Label>
          <select
            id="terminalProfileId"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={terminalProfileId}
            onChange={(e) => setTerminalProfileId(e.target.value)}
          >
            <option value="">{t('certificates.detail.unbound', '— Unbound (library) —')}</option>
            {(profiles ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="serialNumber">
            {t('certificates.upload.serialOptional', 'Serial number (optional)')}
          </Label>
          <Input
            id="serialNumber"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="notBefore">
            {t('certificates.upload.notBeforeOptional', 'Not before (optional)')}
          </Label>
          <Input
            id="notBefore"
            type="date"
            value={notBefore}
            onChange={(e) => setNotBefore(e.target.value)}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="notAfter">
            {t('certificates.upload.notAfterOptional', 'Not after (optional)')}
          </Label>
          <Input
            id="notAfter"
            type="date"
            value={notAfter}
            onChange={(e) => setNotAfter(e.target.value)}
            className="mt-1"
          />
        </div>

        <div className="col-span-2">
          <Label htmlFor="file">{t('certificates.upload.file', 'Certificate file')}</Label>
          <Input
            id="file"
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            required
            className="mt-1"
          />
          {file !== null && (
            <p className="mt-1 text-xs text-muted-foreground">
              {file.name} · {file.size.toLocaleString()} {t('certificates.upload.bytes', 'bytes')}
            </p>
          )}
        </div>

        {banner !== null && (
          <p className="col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {banner}
          </p>
        )}

        <div className="col-span-2 flex gap-2">
          <Button type="submit" disabled={upload.isPending}>
            {upload.isPending
              ? t('certificates.upload.uploading', 'Uploading…')
              : t('certificates.upload.title', 'Upload certificate')}
          </Button>
          <Button asChild variant="ghost">
            <Link to="/certificates">{t('common.cancel', 'Cancel')}</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}
